import { attach, createEvent, type EventPayload, sample } from 'effector'
import { condition, not, snapshot } from 'patronum'

import { $portalProfile, $portalProfileHasEmail, portalProfileAPI } from '@/entities/profile/portal'
import { $lang, attachI18n } from '@/shared/lib/i18n'
import { toast } from '@/shared/lib/toast'
import { type NotificationArgs, showNotificationFx } from '@/shared/ui'

import { LinkEmailDialog } from './link-email-dialog'

// GIVE CONSENT

type ConsentSource = 'profile settings' | 'in-game form'

export const giveConsentRequested = createEvent<{ source: ConsentSource }>()
export const revokeConsentRequested = createEvent()

const giveConsentFx = attach({
    effect: portalProfileAPI.notificationEmailGiveConsent,
    source: $lang,
    mapParams: ({ source }: EventPayload<typeof giveConsentRequested>, lang) => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

        return {
            lang,
            source,
            timezone,
        }
    },
})

// User is resubscribing if they already unsubscribed and are now subscribing again
const $isResubscribe = snapshot({
    source: $portalProfile,
    clock: giveConsentFx,
    fn: (profile) => Boolean(profile?.email_consent_info?.opt_in_time),
})

condition({
    source: giveConsentRequested,
    if: $portalProfileHasEmail,
    then: giveConsentFx,
    else: LinkEmailDialog.model.showFx,
})

sample({
    clock: giveConsentFx.failData,
    target: attachI18n(toast.errorFx, 'TEXT_TOAST_NEWSLETTER_SUBSCRIBE_ERROR'),
})

export const subscribeAttemptFailed = giveConsentFx.fail
export const $subscribePending = giveConsentFx.pending

sample({
    clock: giveConsentFx.done,
    filter: not($isResubscribe),
    source: $portalProfile,
    fn: (portalProfile): NotificationArgs => ({ notification: 'signup_thank', email: portalProfile?.email ?? '' }),
    target: showNotificationFx,
})

// REVOKE CONSENT

const revokeConsentFx = attach({ effect: portalProfileAPI.notificationEmailRevokeConsent })

export const unsubscribeAttemptFailed = revokeConsentFx.fail
export const $unsubscribePending = revokeConsentFx.pending

sample({
    clock: revokeConsentFx.failData,
    target: attachI18n(toast.errorFx, 'TEXT_TOAST_NEWSLETTER_UNSUBSCRIBE_ERROR'),
})

sample({
    source: revokeConsentRequested,
    target: revokeConsentFx,
})
